<script>
import formInput from "@/mixins/shared/formInput";
import assetMetadataValidity from "@wielink-application/asset/js/mixins/assetMetadataValidity";
import {useDropzone, convertFileToBase64} from "@wielink-application/shared/js/composables/useDropzone";
import AssetableDatatable from "@/pages/assets/AssetableDatatable.vue";
import AssetableModal from "@/pages/assets/AssetableModal.vue";

export default {
    mixins: [formInput, assetMetadataValidity],
    components: {AssetableDatatable, AssetableModal},
    props: {
        title: {
            type: String,
            required: true,
        },
        assetableGroup: {
            type: String,
            required: true,
        },
        assetableType: {
            type: String,
            required: true,
        },
        tableConfig: {
            type: [String, Object],
            required: false,
            default: null,
        },
        accept: {
            type: String,
            required: false,
            default: null,
        },
    },
    inject: {
        registerAssetField: {default: null},
        unregisterAssetField: {default: null},
    },
    computed: {
        rows() {
            let assets = Array.isArray(this.modelValue) ? this.modelValue : [];
            return assets
                .map((asset, index) => ({...asset, _index: index}))
                .filter((row) => !row.deleted)
                .map((row, displayIndex) => ({
                    ...row,
                    title: row.title ?? row.name,
                    created_at: row.created_at ?? new Date().toISOString(),
                    prio: displayIndex,
                    _key: this.renderedId + '-' + (row.id ?? 'pending-' + row._index),
                    _missingMetadata: this.missesRequiredMetadata(row),
                }));
        }
    },
    mounted() {
        this.registerAssetField?.({
            name: this.name,
            assetableType: this.assetableType,
            assetableGroup: this.assetableGroup
        });

        useDropzone({
            scope: this.$refs.dropzone,
            input: this.$refs.dropzoneInput,
            accept: this.accept,
            onFiles: (files) => this.addDroppedFiles(files),
            onRejected: () => Helpers.Message.error('Dit bestandstype is hier niet toegestaan.'),
        });
    },
    beforeUnmount() {
        this.unregisterAssetField?.(this.name);
    },
    methods: {
        async addDroppedFiles(files) {
            // Meestal al gecached; alleen relevant als er gedropt wordt voordat de definities binnen zijn
            await this.fetchMetadataFields(this.assetableGroup);

            for (let file of files) {
                this.addAsset({
                    id: null,
                    base64: await convertFileToBase64(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                    title: file.name.replace(/\..*$/, ''),
                    metadata: this.prepareMetadata({}),
                });
            }
        },
        newAsset() {
            this.$refs.assetableModal.newAsset();
        },
        editAsset(row) {
            this.$refs.assetableModal.editAsset(this.modelValue[row._index], row._index);
        },
        async deleteAsset(row) {
            let result = await Helpers.Alert.confirm('Weet u zeker dat u dit bestand wilt verwijderen?');
            if (!result) {
                return;
            }

            let assets = this.modelValue;
            if (row.id) {
                assets[row._index].deleted = true;
            } else {
                assets.splice(row._index, 1);
            }
            this.changeModelValueTo(assets);
        },
        previewAsset(row) {
            if (!row.id) {
                Helpers.Message.warning('Sla het formulier eerst op om dit bestand te bekijken.');
                return;
            }
            window.open(route('api.v1.assets.preview', {asset: row.id}), '_blank');
        },
        addAsset(asset) {
            let assets = Array.isArray(this.modelValue) ? this.modelValue : [];
            assets.push(asset);
            this.changeModelValueTo(assets);
        },
        updateAsset(data) {
            let assets = this.modelValue;
            let asset = {...assets[data.index], ...data.asset};
            if (asset.id) {
                asset.dirty = true;
            }
            assets[data.index] = asset;
            this.changeModelValueTo(assets);
        },
        reorderAssets(orderedRows) {
            let assets = this.modelValue;
            let newValue = orderedRows.map((row) => assets[row._index]);
            newValue.push(...assets.filter((asset) => asset.deleted));
            this.changeModelValueTo(newValue);
        }
    }
}
</script>

<template>
    <Card>
        <div class="d-flex flex-row flex-wrap gap-3 mb-3 align-items-center justify-content-between">
            <h2 class="card-title m-0">{{ this.title }}</h2>
            <div class="d-flex flex-row flex-wrap column-gap-3 row-gap-2">
                <button @click.prevent="this.newAsset()" class="btn btn-primary">
                    <i class="bi bi-plus-lg"></i>
                    Nieuw
                </button>
            </div>
        </div>

        <div ref="dropzone" class="upload-container mb-3">
            <p class="m-0">Sleep bestanden hierheen of klik om te uploaden</p>
            <input ref="dropzoneInput" type="file" :accept="accept" :id="renderedId + '_dropzone'" multiple hidden>
        </div>

        <AssetableDatatable
            :id="renderedId"
            :rows="rows"
            :table-config="tableConfig"
            @edit:asset="editAsset"
            @delete:asset="deleteAsset"
            @preview:asset="previewAsset"
            @reorder:assets="reorderAssets"
        />

        <label v-if="hasError" class="text-danger mt-2 small">{{ this.form.errors[this.name] }}</label>

        <AssetableModal
            :id="renderedId + '_modal'"
            :assetable-group="this.assetableGroup"
            :assetable-type="this.assetableType"
            :accept="accept"
            ref="assetableModal"
            @created:asset="addAsset"
            @updated:asset="updateAsset"
        />
    </Card>
</template>
